'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useAuth } from '@/platform/auth/useAuth';
import { getPublicProfile, userProfileToPublic, type PublicProfile } from '@/platform/users/api';
import {
  formatFeedAuthorMeta,
  formatFeedTime,
  resolveFeedAuthorDisplay,
} from './feedUtils';

type Props = {
  userId: string;
  createdAt?: string;
  meta?: string;
  /** Product tour: skip profile API and use this display data. */
  fixtureProfile?: { displayName?: string; handle?: string; avatarUrl?: string };
  /** When true, avatar/name are not links (static fixtures without in-app navigation). */
  disableProfileLink?: boolean;
  /** Tour / in-app: open profile without changing the browser URL. */
  onSelectAuthor?: (userId: string) => void;
};

export function FeedAuthor({
  userId,
  createdAt,
  meta,
  fixtureProfile,
  disableProfileLink = false,
  onSelectAuthor,
}: Props) {
  const { user, profile } = useAuth();
  const [publicProfile, setPublicProfile] = useState<PublicProfile | null>(null);
  // Relative labels use Date.now()/locale — must match SSR HTML. Defer until mount.
  const [time, setTime] = useState('');
  const isSelf = user?.uid === userId;

  useEffect(() => {
    setTime(formatFeedTime(createdAt));
  }, [createdAt]);

  useEffect(() => {
    if (fixtureProfile) {
      setPublicProfile({
        id: userId,
        displayName: fixtureProfile.displayName,
        handle: fixtureProfile.handle,
        avatarUrl: fixtureProfile.avatarUrl,
      });
      return;
    }
    // Tour fixtures always pass fixtureProfile — never hit marketing host /api.
    if (process.env.NEXT_PUBLIC_TOUR_BUILD === 'true') {
      setPublicProfile(null);
      return;
    }
    if (isSelf && profile) {
      setPublicProfile({
        ...userProfileToPublic(profile),
        avatarUrl: profile.avatarUrl ?? user?.photoURL ?? undefined,
      });
      return;
    }
    let cancelled = false;
    getPublicProfile(userId)
      .then((p) => {
        if (!cancelled) setPublicProfile(p);
      })
      .catch(() => {
        if (!cancelled) setPublicProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, isSelf, profile, user?.photoURL, fixtureProfile]);

  const authPhoto = isSelf ? user?.photoURL : undefined;
  const { name, handle, displayName, initial, avatarUrl } = resolveFeedAuthorDisplay(
    userId,
    fixtureProfile ?? publicProfile,
    authPhoto,
  );
  const metaLine = formatFeedAuthorMeta(handle, displayName, time, meta);
  const useInAppProfile = !!onSelectAuthor;
  const noProfileLink =
    !useInAppProfile &&
    (disableProfileLink || !!fixtureProfile || process.env.NEXT_PUBLIC_TOUR_BUILD === 'true');

  const body = (
    <>
      <div className="profile-photo feed-card__avatar" aria-hidden>
        {avatarUrl ? <img src={avatarUrl} alt="" /> : initial}
      </div>
      <div className="feed-card__author-text">
        <span className="feed-card__author-name">{name}</span>
        {metaLine && <span className="feed-card__author-meta text-muted">{metaLine}</span>}
      </div>
    </>
  );

  return (
    <header className="feed-card__author">
      {useInAppProfile ? (
        <button
          type="button"
          className="feed-card__author-link tour-text-btn"
          onClick={() => onSelectAuthor(userId)}
        >
          {body}
        </button>
      ) : noProfileLink ? (
        <div className="feed-card__author-link">{body}</div>
      ) : (
        <Link href={`/users/${userId}/`} className="feed-card__author-link" prefetch={false}>
          {body}
        </Link>
      )}
    </header>
  );
}
